import React, { useEffect, useState } from "react";
import { Chart as ChartJS, ArcElement, Tooltip } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Pie } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, ChartDataLabels);

const COLORS = [
  "#FFCE56",
  "#FF9F40",
  "#36A2EB",
  "#4BC0C0",
  "#FF6384",
  "#9966FF",
  "#2ecc71",
];

function ByGeography({ chart_data }) {
  const [label, setLabel] = useState([]);
  const [values, setValues] = useState([]);
  const [data, setData] = useState({labels: [], datasets: []});
  const [options, setOptions] = useState({});
  const [actualValues, setActualValues] = useState([]);

  useEffect(() => {
    let regions = [];
    let percentages = [];
    let actualAmounts = [];

    let europeTotal = 0;
    let europeAmount = 0;

    let user = {};
    let currencies = [];

    try {
      user = JSON.parse(localStorage.getItem("user") || "{}");
      currencies = JSON.parse(localStorage.getItem("currency_rates") || "[]");
    } catch (error) {
      console.error("Error reading currency data:", error);
    }
    const defaultCurrencyId = Number(user?.default_currency_id || 1);
    const selectedCurrency = currencies.find((currency) => Number(currency.value) === defaultCurrencyId);
    const symbol = selectedCurrency?.symbol || (defaultCurrencyId === 1 ? "₹" : "");
    const conversionRateToINR = Number(selectedCurrency?.conversion_rate_to_inr) || 1;
    const amountDivisor = defaultCurrencyId === 1 ? 10000000 : 1000;

    const total_amount =
      chart_data?.reduce((sum, item) => sum + Number(item.total_amount || 0), 0) || 0;

    if (total_amount === 0) {
      setLabel([]);
      setValues([]);
      setActualValues([]);
      return;
    }

    // ------------------------------------
    // Process region data
    // ------------------------------------
    chart_data?.forEach((item) => {
      const totalAmount = Number(
        item.total_amount || 0
      );

      // Percentage should always be based on INR
      const percentage =
        (totalAmount / total_amount) * 100;

      // Convert INR -> selected currency
      const convertedAmount =
        totalAmount / conversionRateToINR;

      // Convert to Cr/K for display
      const displayAmount =
        convertedAmount / amountDivisor;

      if (item.id === 1) {
        regions.push("MEA");
        percentages.push(
          percentage.toFixed(1)
        );
        actualAmounts.push(displayAmount);

      } else if (item.id === 4 || item.id === 10) {
        europeTotal += percentage;
        europeAmount += displayAmount;

      } else if (item.id === 6) {
        regions.push("Americas");
        percentages.push(
          percentage.toFixed(1)
        );
        actualAmounts.push(displayAmount);

      } else if (item.id === 8) {
        regions.push("SEA");
        percentages.push(
          percentage.toFixed(1)
        );
        actualAmounts.push(displayAmount);

      } else {
        regions.push(item.region_name);
        percentages.push(
          percentage.toFixed(1)
        );
        actualAmounts.push(displayAmount);
      }
    });

    // ------------------------------------
    // Add Europe
    // ------------------------------------
    if (europeTotal > 0) {
      regions.push("Europe");
      percentages.push(
        europeTotal.toFixed(1)
      );
      actualAmounts.push(europeAmount);
    }

    setLabel(regions);
    setValues(percentages);
    setActualValues(actualAmounts);

    // ------------------------------------
    // Store currency information for chart
    // ------------------------------------
    setData({
      labels: regions,
      datasets: [
        {
          data: percentages,
          actualData: actualAmounts,
          backgroundColor: COLORS.slice(
            0,
            percentages.length
          ),
          borderWidth: 0,
        },
      ],
    });

    setOptions({
      responsive: true,
      maintainAspectRatio: false,

      layout: {
        padding: {
          top: 12,
          bottom: 12,
          left: 12,
          right: 12,
        },
      },

      plugins: {
        legend: {
          display: true,
          position: "right",

          labels: {
            boxWidth: 10,
            boxHeight: 10,
            padding: 4,

            font: {
              size: 12,
            },
          },
        },

        datalabels: {
          color: "#000",
          anchor: "end",
          align: "end",
          offset: -3,

          formatter: (value) => {
            return Number(value) > 1
              ? `${value}%`
              : "";
          },

          font: {
            weight: "bold",
            size: 10,
          },
        },

        tooltip: {
          callbacks: {
            label: (context) => {
              const actual = context.dataset.actualData?.[context.dataIndex] || 0;
              const percentage = Number(context.parsed || 0);
              return `${percentage.toFixed(1)}% : ${symbol} ${actual.toFixed(
                2
              )} ${defaultCurrencyId === 1 ? "Cr" : "K"}`;
            },
          },
        },
      },

      onHover: (event, elements) => {
        if (elements.length > 0) {
          event.native.target.style.cursor =
            "pointer";
        } else {
          event.native.target.style.cursor =
            "default";
        }
      },
    });
  }, [chart_data]);

  return (
    <div
      style={{
        display: "flex",
        width: "280px",
        height: "220px",
        alignItems: "center",
      }}
    >
      {data?.datasets?.length > 0 && (
        <Pie
          data={data}
          options={options}
        />
      )}
    </div>
  );
}

export default ByGeography;